import { useMutation, useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { SimpleEditor } from "src/components/Default/LekUI/Editor";
import { LekAccordion } from "src/components/Default/LekUI/NewAccordion";
import { FolderNoteCard } from "src/components/NewComponents/Exam/OptionDetails/FolderNoteCard";
import { getService } from "src/helpers/isPes";
import EditIcon from "src/images/Icons/newIcons/exam/more/edit.svg";
import { queryClient } from "src/queryClient";
import newApi from "src/newApi";

interface FolderInfo {
	id: string;
	name: string;
	details?: { color?: string | null } | null;
}

interface NoteItem {
	data?: { id: string | number; name?: string; note?: string };
	folderId: string;
	folder: FolderInfo;
}

interface LessonNoteProps {
	lesson: unknown;
	closeNotes: () => void;
	folders?: FolderInfo[];
}

export const LessonNote = ({ closeNotes, folders = [] }: LessonNoteProps) => {
	const { t } = useTranslation();
	const folderIds = folders.map((f) => String(f.id));

	const { mutateAsync: createNote, isPending: isCreating } = useMutation<
		unknown,
		unknown,
		{ note: string; folderId: string; name: string }
	>({
		mutationFn: ({ note, folderId, name }) =>
			newApi.createUserNote({ note, folderId, name }),
		onSuccess: () => {
			folderIds.forEach((folderId) => {
				queryClient.invalidateQueries({
					queryKey: ["folder-notes", getService(), folderId],
				});
			});
		},
	});

	const folderQueries = useQueries({
		queries: folderIds.map((folderId) => ({
			queryKey: ["folder-notes", getService(), folderId],

			queryFn: () =>
				newApi.getFolderItemsOffset(folderId, {
					type: "note",
					take: 50,
				}),

			enabled: !!folderId,
		})),
	});

	const allNotes: NoteItem[] = folderQueries.flatMap((query, idx) => {
		const items = (query.data as { data?: unknown[] })?.data || [];
		return items.map((item) => ({
			...(item as object),
			folderId: folderIds[idx],
			folder: folders[idx],
		})) as NoteItem[];
	});

	const handleAddNote = async (html: string) => {
		if (!html || !folderIds.length) return;
		await createNote({ note: html, folderId: folderIds[0], name: "" });
	};

	return (
		<LekAccordion title={t("exam.notes.title")} TitleIcon={EditIcon}>
			<div className="flex flex-col gap-32">
				<SimpleEditor
					onSubmit={handleAddNote}
					isLoading={isCreating}
					clearAfterSave
					placeholder={t(
						"exam.notes.placeholder",
						"Dodaj notatkę lub zdjęcie...",
					)}
					submitText={t("exam.notes.addNote", "Dodaj notatkę")}
					onCancel={closeNotes}
					cancelText={t("commands.cancel")}
				/>
				{allNotes.length > 0 && (
					<div className="flex flex-col gap-20">
						<div className="flex items-center justify-between">
							<span>
								{t("exam.notes.myNotesInFolder", "Moje notatki w folderze")}
							</span>
						</div>
						<div className="flex flex-col gap-16">
							{allNotes.map((note, idx) => (
								<FolderNoteCard
									key={idx}
									note={note.data ?? null}
									folder={note.folder}
								/>
							))}
						</div>
					</div>
				)}
			</div>
		</LekAccordion>
	);
};
